/**
 * One canonical URL per language.
 *
 * - Polish pages (/pl/...) use Polish slugs.
 * - All other languages (/en, /de, /fr, /it) use the English slugs.
 *
 * Every other slug variant still works but redirects to the canonical one,
 * and hreflang always points at canonical URLs only.
 */

export const LANGS = ["pl", "en", "de", "fr", "it"] as const;
export type Lang = (typeof LANGS)[number];

export const BASE_URL = "https://www.stickerstattooo.pl";

type SlugPair = { pl: string; intl: string };

/** key -> canonical slug per language group */
export const ROUTE_SLUGS: Record<string, SlugPair> = {
  home: { pl: "", intl: "" },
  creator: { pl: "kreator", intl: "kreator" },
  sheets: { pl: "kreator/arkusze", intl: "kreator/arkusze" },
  blog: { pl: "blog", intl: "blog" },
  faq: { pl: "faq", intl: "faq" },
  foils: { pl: "folie-i-laminaty", intl: "foils-and-laminates" },
  portfolio: { pl: "portfolio", intl: "portfolio" },
  contact: { pl: "kontakt", intl: "contact" },
  about: { pl: "o-nas", intl: "about" },
  cart: { pl: "koszyk", intl: "cart" },
  checkout: { pl: "checkout", intl: "checkout" },
  terms: { pl: "regulamin", intl: "terms" },
  privacy: { pl: "polityka-prywatnosci", intl: "privacy-policy" },
  cookies: { pl: "polityka-cookies", intl: "cookie-policy" },
  withdrawal: { pl: "odstapienie", intl: "withdrawal" },
  quickPayment: { pl: "szybka-platnosc", intl: "quick-payment" },
  paymentStatus: { pl: "platnosc-status", intl: "payment-status" },
  login: { pl: "logowanie", intl: "login" },
  account: { pl: "konto", intl: "account" },
  resetPassword: { pl: "reset-haslo", intl: "reset-password" },
  receiveFile: { pl: "receive-file", intl: "receive-file" },
};

/** Extra non-canonical slugs that must still resolve (and redirect). */
const EXTRA_ALIASES: Record<string, string> = {
  realizacje: "portfolio",
};

/** slug -> route key (all known variants) */
const SLUG_TO_KEY: Record<string, string> = (() => {
  const map: Record<string, string> = { ...EXTRA_ALIASES };
  for (const [key, pair] of Object.entries(ROUTE_SLUGS)) {
    if (pair.pl) map[pair.pl] = key;
    if (pair.intl) map[pair.intl] = key;
  }
  return map;
})();

/** Slugs sorted longest-first so "kreator/arkusze" wins over "kreator". */
const SLUGS_BY_LENGTH = Object.keys(SLUG_TO_KEY).sort((a, b) => b.split("/").length - a.split("/").length || b.length - a.length);

export function canonicalSlug(key: string, lang: string): string {
  const pair = ROUTE_SLUGS[key];
  if (!pair) return "";
  return lang === "pl" ? pair.pl : pair.intl;
}

export function stripLang(pathname: string): string {
  return pathname.replace(/^\/(pl|en|de|fr|it)(?=\/|$)/, "").replace(/^\//, "").replace(/\/$/, "");
}

/** Matches a language-less path to a route key plus any trailing segments (e.g. blog slug). */
export function matchRoute(pathWithoutLang: string): { key: string; rest: string } | null {
  const clean = pathWithoutLang.replace(/^\//, "").replace(/\/$/, "");
  if (!clean) return { key: "home", rest: "" };
  for (const slug of SLUGS_BY_LENGTH) {
    if (clean === slug) return { key: SLUG_TO_KEY[slug], rest: "" };
    if (clean.startsWith(`${slug}/`)) return { key: SLUG_TO_KEY[slug], rest: clean.slice(slug.length + 1) };
  }
  return null;
}

/** Canonical language-less path for a route key. */
function canonicalSuffix(key: string, rest: string, lang: string): string {
  const slug = canonicalSlug(key, lang);
  const joined = [slug, rest].filter(Boolean).join("/");
  return joined;
}

/** Canonical absolute URL for the given language of the page currently at `pathname`. */
export function canonicalUrlFor(pathname: string, lang: string): string | null {
  const match = matchRoute(stripLang(pathname));
  if (!match) return null;
  const suffix = canonicalSuffix(match.key, match.rest, lang);
  return `${BASE_URL}/${lang}${suffix ? `/${suffix}` : "/"}`;
}

/** Canonical in-app path (with language prefix) — used for redirects. */
export function canonicalPathFor(pathname: string, lang: string): string | null {
  const match = matchRoute(stripLang(pathname));
  if (!match) return null;
  const suffix = canonicalSuffix(match.key, match.rest, lang);
  return `/${lang}${suffix ? `/${suffix}` : "/"}`;
}

/** Full hreflang map (all languages + x-default) built from canonical URLs only. */
export function alternatesFor(pathname: string): Record<string, string> | null {
  const match = matchRoute(stripLang(pathname));
  if (!match) return null;
  const out: Record<string, string> = {};
  for (const l of LANGS) {
    const suffix = canonicalSuffix(match.key, match.rest, l);
    out[l] = `${BASE_URL}/${l}${suffix ? `/${suffix}` : "/"}`;
  }
  out["x-default"] = out.pl;
  return out;
}
